import {
  loadingDepartments,
  setDepartments,
  setLoadingDeleteDepartment,
  setLoadingEditAddDepartment,
  removeDepartmentFromList,
  setEditedDepartment,
} from './departments.actions';
import {
  handleAddDepartment,
  handleDeleteDepartment,
  handleEditDepartment,
  handleFetchDepartments,
  handleUploadTeamImage,
} from './departments.helpers';
import {
  createDepartmentToAddObject,
  createDepartmentToEditObject,
} from './departments.adapter';
import departmentTypes from './departments.types';
import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import { getNormalizedUrl, handleErrors, getNotifMsg } from '../../utils';

const mapState = ({ appData }) => ({
  locale: appData.locale,
});

export function* fetchDepartments({ payload }) {
  const { locale } = yield select(mapState);

  try {
    yield put(loadingDepartments());
    const { data } = yield handleFetchDepartments(payload);
    yield put(setDepartments(data));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* addNewDepartment({ payload }) {
  const { locale } = yield select(mapState);

  try {
    yield put(setLoadingEditAddDepartment(true));
    const logo = payload.department.logo;
    let returnedUploadPath = null;
    if (logo) {
      returnedUploadPath = yield handleUploadTeamImage(logo);
      if (!returnedUploadPath.success) {
        toast.error(getNotifMsg('UPLOAD_IMAGE_NOT_SUCCESSFUL', locale));
        return;
      }
    }
    const { toggleModal, department } = payload;
    const departmentObject = createDepartmentToAddObject(
      department,
      returnedUploadPath
    );
    const departmentData = yield handleAddDepartment(departmentObject);

    yield put(setLoadingEditAddDepartment(false));
    if (!departmentData.success) {
      toast.error(getNotifMsg('PROBLEM_CREATING_TEAM', locale), {
        position: 'bottom-center',
      });
      return;
    }
    yield put(setDepartments(departmentData.data));
    toast.success(getNotifMsg('TEAM_CREATE_SUCCESS', locale));
    toggleModal();
  } catch (error) {
    yield put(setLoadingEditAddDepartment(false));
    if (error.response?.status === 422) {
      toast.error(getNotifMsg('FILL_EMPTY_FIELDS', locale));
      return;
    }
    handleErrors(error, locale);
  }
}

export function* editDepartment({ payload }) {
  const { locale } = yield select(mapState);

  try {
    yield put(setLoadingEditAddDepartment(true));
    const { department, toggleModal } = payload;
    const { logo } = department;
    let returnedUploadPath = logo;

    // check if logo has changed
    if (typeof logo === 'object') {
      const uploadResponse = yield handleUploadTeamImage(logo);
      if (!uploadResponse.success) {
        toast.error(getNotifMsg('UPLOAD_IMAGE_NOT_SUCCESSFUL', locale));
        return;
      }
      returnedUploadPath = getNormalizedUrl(uploadResponse.filePath);
    }
    const departmentData = yield handleEditDepartment(
      createDepartmentToEditObject(department, returnedUploadPath)
    );
    if (!departmentData.success) {
      toast.error(getNotifMsg('PROBLEM_EDITING_TEAM', locale), {
        position: 'bottom-center',
      });
      yield put(setLoadingEditAddDepartment(false));
      return;
    }
    yield put(setEditedDepartment(departmentData.data.teams));
    yield put(setLoadingEditAddDepartment(false));
    toast.success(getNotifMsg('SUCCESSFULL_EDITING', locale));
    toggleModal();
  } catch (error) {
    yield put(setLoadingEditAddDepartment(false));
    if (error.response?.status === 422) {
      toast.error(getNotifMsg('FILL_EMPTY_FIELDS', locale));
      return;
    }
    handleErrors(error, locale);
  }
}

export function* deleteDepartment({ payload }) {
  const { locale } = yield select(mapState);

  try {
    const { toggleModal, department } = payload;
    yield put(setLoadingDeleteDepartment(true));
    const departmentData = yield handleDeleteDepartment(department);
    yield put(removeDepartmentFromList(department.teamIds[0]));
    if (!departmentData.success) {
      toast.error(getNotifMsg('PROBLEM_REMOVING_TEAM', locale), {
        position: 'bottom-center',
      });
      return;
    }
    toast.success(getNotifMsg('SUCCESSFUL_REMOVING_TEAM', locale));
    toggleModal();
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    yield put(setLoadingDeleteDepartment(false));
  }
}

export function* onFetchDepartmentsStart() {
  yield takeLatest(departmentTypes.FETCH_DEPARTMENTS_START, fetchDepartments);
}

export function* onAddNewDepartmentStart() {
  yield takeLatest(departmentTypes.ADD_DEPARTMENT_START, addNewDepartment);
}

export function* onEditDepartmentStart() {
  yield takeLatest(departmentTypes.EDIT_DEPARTMENT_START, editDepartment);
}

export function* onDeleteDepartmentStart() {
  yield takeLatest(departmentTypes.DELETE_DEPARTMENT_START, deleteDepartment);
}

export default function* departmentsSagas() {
  yield all([
    call(onFetchDepartmentsStart),
    call(onAddNewDepartmentStart),
    call(onEditDepartmentStart),
    call(onDeleteDepartmentStart),
  ]);
}
